import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'

interface LazyRouteErrorBoundaryProps {
  children: ReactNode
  title?: string
  retryButtonLabel?: string
}

interface LazyRouteErrorBoundaryState {
  hasError: boolean
  errorMessage: string | null
  retryKey: number
}

export default class LazyRouteErrorBoundary extends Component<LazyRouteErrorBoundaryProps, LazyRouteErrorBoundaryState> {
  constructor(props: LazyRouteErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      errorMessage: null,
      retryKey: 0
    }
  }

  static getDerivedStateFromError(error: unknown): Partial<LazyRouteErrorBoundaryState> {
    return {
      hasError: true,
      errorMessage: error instanceof Error ? error.message : 'Не удалось загрузить модуль.'
    }
  }

  componentDidCatch(): void {}

  private handleRetry = (): void => {
    this.setState((prev) => ({
      hasError: false,
      errorMessage: null,
      retryKey: prev.retryKey + 1
    }))
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="h6">{this.props.title || 'Раздел временно недоступен'}</Typography>
            <Alert severity="error">
              {this.state.errorMessage || 'Не удалось загрузить модуль интерфейса.'}
            </Alert>
            <Box>
              <Button variant="contained" onClick={this.handleRetry}>
                {this.props.retryButtonLabel || 'Повторить'}
              </Button>
            </Box>
          </Stack>
        </Box>
      )
    }

    return (
      <Box key={this.state.retryKey}>
        {this.props.children}
      </Box>
    )
  }
}
